import { FC, useState } from 'react';
import { Stack, Text, Skeleton, Divider } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Patient, DiagnosticReport } from '@medplum/fhirtypes';
import { useCurrentEncounter } from '@/hooks/useCurrentEncounter';
import { ConfirmCloseDialog } from '@/components/ConfirmCloseDialog';
import { BirthMeasurementsList } from './BirthMeasurementsList';
import { usePrenatalPeriodResponse } from '../hooks/usePrenatalPeriodResponse';
import { usePerinatalPeriodResponse } from '../hooks/usePerinatalPeriodResponse';
import { usePrenatalChecks } from '../hooks/usePrenatalChecks';
import { usePreImplantationChecks } from '../hooks/usePreImplantationChecks';
import { useBirthObservations } from '../hooks/useBirthObservations';
import { PrenatalPeriodModal } from '../modals/PrenatalPeriodModal';
import { PerinatalPeriodModal } from '../modals/PerinatalPeriodModal';
import { PrenatalCheckModal } from '../modals/PrenatalCheckModal';
import { PreImplantationCheckModal } from '../modals/PreImplantationCheckModal';
import { BirthMeasurementsModal } from '../modals/BirthMeasurementsModal';
import ChecksList from './ChecksList';
import BirthPeriodList from './BirthPeriodList';

interface MedicalHistorySectionsProps {
  patient: Patient;
  readonly?: boolean;
}

export const MedicalHistorySections: FC<MedicalHistorySectionsProps> = ({
  patient,
  readonly = false,
}) => {
  const { t } = useTranslation(['patient-pages']);
  const { currentEncounter } = useCurrentEncounter(patient);

  // Modal states
  const [prenatalPeriodModalOpen, setPrenatalPeriodModalOpen] = useState(false);
  const [perinatalPeriodModalOpen, setPerinatalPeriodModalOpen] =
    useState(false);
  const [prenatalCheckModalOpen, setPrenatalCheckModalOpen] = useState(false);
  const [preImplantationModalOpen, setPreImplantationModalOpen] =
    useState(false);
  const [birthMeasurementsModalOpen, setBirthMeasurementsModalOpen] =
    useState(false);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);

  // Selected items for editing
  const [selectedPrenatalCheck, setSelectedPrenatalCheck] =
    useState<DiagnosticReport | null>(null);
  const [selectedPreImplantation, setSelectedPreImplantation] =
    useState<DiagnosticReport | null>(null);

  // Delete states
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<
    'prenatal' | 'preimplantation' | null
  >(null);

  // Data hooks
  const {
    questionnaire: prenatalPeriodQuestionnaire,
    questionnaireResponse: prenatalPeriodResponse,
    loading: prenatalPeriodLoading,
    refetch: refetchPrenatalPeriod,
    deleteResponse: deletePrenatalPeriodResponse,
    isIVFConception,
  } = usePrenatalPeriodResponse(patient);

  const {
    questionnaire: perinatalPeriodQuestionnaire,
    questionnaireResponse: perinatalPeriodResponse,
    loading: perinatalPeriodLoading,
    refetch: refetchPerinatalPeriod,
    deleteResponse: deletePerinatalPeriodResponse,
  } = usePerinatalPeriodResponse(patient);

  const {
    checks: prenatalChecks,
    loading: prenatalChecksLoading,
    deleteCheck,
    refetch: refetchPrenatalChecks,
  } = usePrenatalChecks(patient, currentEncounter?.id);

  const {
    data: birthObservations,
    loading: birthObservationsLoading,
    saveBirthMeasurements,
    deleteBirthMeasurements,
    refetch: refetchBirthObservations,
  } = useBirthObservations(patient);

  const {
    checks: preImplantationChecks,
    loading: preImplantationLoading,
    deleteCheck: deletePreImplantationCheck,
    refetch: refetchPreImplantation,
  } = usePreImplantationChecks(patient, currentEncounter?.id);

  // Handle delete click - show confirmation dialog
  const handleDeleteClick = (
    id: string,
    type: 'prenatal' | 'preimplantation'
  ) => {
    setPendingDeleteId(id);
    setDeleteType(type);
    setDeleteConfirmOpened(true);
  };

  // Handle confirmed delete action
  const handleConfirmDelete = async () => {
    if (!pendingDeleteId || !deleteType) {
      return;
    }

    setDeleteConfirmOpened(false);
    setDeletingId(pendingDeleteId);

    try {
      if (deleteType === 'prenatal') {
        await deleteCheck(pendingDeleteId);
      } else {
        await deletePreImplantationCheck(pendingDeleteId);
      }

      notifications.show({
        color: 'green',
        title: t(
          deleteType === 'prenatal'
            ? 'medicalHistory.prenatalChecks.notifications.deleteSuccess'
            : 'medicalHistory.preImplantationChecks.notifications.deleteSuccess',
          {
            defaultValue: 'Check deleted',
          }
        ),
        message: t(
          deleteType === 'prenatal'
            ? 'medicalHistory.prenatalChecks.notifications.deleteSuccessMessage'
            : 'medicalHistory.preImplantationChecks.notifications.deleteSuccessMessage',
          {
            defaultValue: 'Check removed successfully',
          }
        ),
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t(
          deleteType === 'prenatal'
            ? 'medicalHistory.prenatalChecks.notifications.deleteError'
            : 'medicalHistory.preImplantationChecks.notifications.deleteError',
          {
            defaultValue: 'Delete failed',
          }
        ),
        message: String(error),
      });
    } finally {
      setDeletingId(null);
      setPendingDeleteId(null);
      setDeleteType(null);
    }
  };

  // Handle cancel delete action
  const handleCancelDelete = () => {
    setDeleteConfirmOpened(false);
    setPendingDeleteId(null);
    setDeleteType(null);
  };

  const isLoading =
    prenatalPeriodLoading ||
    perinatalPeriodLoading ||
    prenatalChecksLoading ||
    preImplantationLoading ||
    birthObservationsLoading;

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={100} radius="md" />
        <Skeleton height={100} radius="md" />
        <Skeleton height={100} radius="md" />
      </Stack>
    );
  }

  return (
    <>
      <Stack gap="lg">
        {/* Prenatal History Section */}
        <Stack gap="md">
          <Text fw={600} size="lg">
            {t('medicalHistory.prenatalHistory', {
              defaultValue: 'Prenatal History',
            })}
          </Text>

          <BirthPeriodList
            periodType="prenatalPeriod"
            questionnaire={prenatalPeriodQuestionnaire}
            questionnaireResponse={prenatalPeriodResponse}
            onAdd={() => setPrenatalPeriodModalOpen(true)}
            onDelete={deletePrenatalPeriodResponse}
            readonly={readonly}
          />

          <ChecksList
            type="prenatalChecks"
            checks={prenatalChecks}
            patient={patient}
            onAdd={() => {
              setSelectedPrenatalCheck(null);
              setPrenatalCheckModalOpen(true);
            }}
            onEdit={(check) => {
              setSelectedPrenatalCheck(check);
              setPrenatalCheckModalOpen(true);
            }}
            onDelete={(id) => handleDeleteClick(id, 'prenatal')}
            deletingId={deletingId}
            readonly={readonly}
          />

          {isIVFConception && (
            <ChecksList
              type="preImplantationChecks"
              checks={preImplantationChecks}
              patient={patient}
              onAdd={() => {
                setSelectedPreImplantation(null);
                setPreImplantationModalOpen(true);
              }}
              onEdit={(check) => {
                setSelectedPreImplantation(check);
                setPreImplantationModalOpen(true);
              }}
              onDelete={(id) => handleDeleteClick(id, 'preimplantation')}
              deletingId={deletingId}
              readonly={readonly}
            />
          )}
        </Stack>

        <Divider />

        {/* Perinatal History Section */}
        <Stack gap="md">
          <Text fw={600} size="lg">
            {t('medicalHistory.perinatalHistory', {
              defaultValue: 'Perinatal History',
            })}
          </Text>

          <BirthPeriodList
            periodType="perinatalPeriod"
            questionnaire={perinatalPeriodQuestionnaire}
            questionnaireResponse={perinatalPeriodResponse}
            onAdd={() => setPerinatalPeriodModalOpen(true)}
            onDelete={deletePerinatalPeriodResponse}
            readonly={readonly}
          />

          <BirthMeasurementsList
            data={birthObservations}
            onAdd={() => setBirthMeasurementsModalOpen(true)}
            onDelete={deleteBirthMeasurements}
            readonly={readonly}
          />
        </Stack>
      </Stack>

      {/* Modals */}
      <PrenatalPeriodModal
        opened={prenatalPeriodModalOpen}
        onClose={() => setPrenatalPeriodModalOpen(false)}
        onSuccess={() => {
          refetchPrenatalPeriod();
          setPrenatalPeriodModalOpen(false);
        }}
        patient={patient}
        encounter={currentEncounter}
        questionnaireResponse={prenatalPeriodResponse}
      />

      <PerinatalPeriodModal
        opened={perinatalPeriodModalOpen}
        onClose={() => setPerinatalPeriodModalOpen(false)}
        onSuccess={() => {
          refetchPerinatalPeriod();
          setPerinatalPeriodModalOpen(false);
        }}
        patient={patient}
        encounter={currentEncounter}
        questionnaireResponse={perinatalPeriodResponse}
      />

      <PrenatalCheckModal
        opened={prenatalCheckModalOpen}
        onClose={() => {
          setPrenatalCheckModalOpen(false);
          setSelectedPrenatalCheck(null);
        }}
        onSuccess={() => {
          refetchPrenatalChecks();
          setPrenatalCheckModalOpen(false);
          setSelectedPrenatalCheck(null);
        }}
        patient={patient}
        encounter={currentEncounter}
        report={selectedPrenatalCheck}
      />

      <BirthMeasurementsModal
        key={`birth-measurements-${birthObservations.birthWeight?.id}-${birthObservations.birthLength?.id}-${birthObservations.birthHeadCircumference?.id}`}
        opened={birthMeasurementsModalOpen}
        onClose={() => setBirthMeasurementsModalOpen(false)}
        onSuccess={() => {
          refetchBirthObservations();
          setBirthMeasurementsModalOpen(false);
        }}
        patient={patient}
        existingData={birthObservations}
        onSave={saveBirthMeasurements}
      />

      <PreImplantationCheckModal
        opened={preImplantationModalOpen}
        onClose={() => {
          setPreImplantationModalOpen(false);
          setSelectedPreImplantation(null);
        }}
        onSuccess={() => {
          refetchPreImplantation();
          setPreImplantationModalOpen(false);
          setSelectedPreImplantation(null);
        }}
        patient={patient}
        encounter={currentEncounter}
        report={selectedPreImplantation}
      />

      <ConfirmCloseDialog
        opened={deleteConfirmOpened}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t(
          deleteType === 'prenatal'
            ? 'medicalHistory.prenatalChecks.confirmDelete.title'
            : 'medicalHistory.preImplantationChecks.confirmDelete.title',
          {
            defaultValue:
              deleteType === 'prenatal'
                ? 'Delete prenatal check?'
                : 'Delete pre-implantation check?',
          }
        )}
        message={t(
          deleteType === 'prenatal'
            ? 'medicalHistory.prenatalChecks.confirmDelete.message'
            : 'medicalHistory.preImplantationChecks.confirmDelete.message',
          {
            defaultValue:
              'Are you sure you want to delete this check? This action cannot be undone.',
          }
        )}
        confirmLabel={t(
          deleteType === 'prenatal'
            ? 'medicalHistory.prenatalChecks.confirmDelete.confirm'
            : 'medicalHistory.preImplantationChecks.confirmDelete.confirm',
          {
            defaultValue: 'Delete',
          }
        )}
        cancelLabel={t(
          deleteType === 'prenatal'
            ? 'medicalHistory.prenatalChecks.confirmDelete.cancel'
            : 'medicalHistory.preImplantationChecks.confirmDelete.cancel',
          {
            defaultValue: 'Cancel',
          }
        )}
        confirmColor="red"
      />
    </>
  );
};

export default MedicalHistorySections;
